/* eslint-disable camelcase */
import React, { useEffect, ReactChild } from "react";
import { useInView, IntersectionOptions } from "react-intersection-observer";
import { JobSearchHit } from "@themuse/design-system";
import { triggerSnowplowImpressionEvent } from "@themuse/snowplow-js-client";

interface IOskar {
    setGlobalUserProperties: (properties: any) => void;
    setGlobalEventProperties: (properties: any) => void;
    capture: (eventType: string, eventProperties: any) => void;
    setEnvironment: (environment: string, url?: string) => void;
    getOskarID: (callback: (oskarID: string) => void) => void;
    getSessionID: (callback: (sessionID: string) => void) => void;
}

interface OskarWindow extends Window {
    OskarTS: IOskar;
}

interface TrackedElementProps {
    children: ReactChild;
    eventName: string;
    eventProps?: Object;
    eventType?: string;
    enabled?: boolean;
    callback?: () => void;
}

interface BaseProps {
    app_version_id: string;
    page_referrer: string;
    query_string: string;
    path: string;
    experiments: any[];
}

declare const window: OskarWindow;

/**
 * TrackedElement: Wraps an element in an intersection observer with tracking props.
 */
const TrackedElement = (props: TrackedElementProps & IntersectionOptions) => {
    const {
        children, eventProps, enabled = true, callback, ...restProps
    } = props;

    const intersectionProps: IntersectionOptions = {
        triggerOnce: true,
        rootMargin: "0px",
        ...restProps
    };
    const { ref, inView, entry } = useInView(intersectionProps);
    let viewed = false;

    const trackingProps: any = { ...eventProps };

    useEffect(() => {
        if (inView && enabled) {
            if (callback) {
                callback();
            }
            if (trackingProps?.sp_impression_component && !viewed) {
                triggerSnowplowImpressionEvent(entry.target as HTMLElement);
                viewed = true;
            }
        }
    }, [inView]);

    return (
        <div
            ref={ref}
            data-tracking-props={JSON.stringify(eventProps)}
        >
            {children}
        </div>
    );
};

/**
 * genJobTrackingProps: Generate the props for a tracked element.
 */
const genJobTrackingProps = (job: JobSearchHit) => ({
    company_id: job.hit.company.legacy_id,
    company_name: job.hit.company.name,
    company_short_name: job.hit.company.short_name,
    job_id: job.hit.legacy_id,
    job_level: job.hit.levels.map((level) => level.code),
    job_locations: job.hit.locations.map((location) => location.address),
    job_name: job.hit.title,
    job_short_name: job.hit.short_title,
    job_type: job.hit.apply_type,
    sp_impression_component: "tile",
    sp_impression_tile_type: "job",
    sp_tile_type: "job",
    sp_tile_entity_id: job.hit.legacy_id.toString(),
    sp_tile_entity_uuid: job.hit.id,
    sp_tile_company_id: job.hit.company.legacy_id.toString(),
    sp_tile_location: "search",
});

export {
    TrackedElement,
    genJobTrackingProps
};

export type {
    BaseProps,
    IOskar,
    OskarWindow,
    TrackedElementProps
};
